import React, { useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TemplateLibraryModal from './components/TemplateLibraryModal';
import InvoicePreviewModal from './components/InvoicePreviewModal';
import EmailDeliveryModal from './components/EmailDeliveryModal';
import ManualInvoiceForm from './components/ManualInvoiceForm';

const InvoiceGenerationCenter = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('generated');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [templateModal, setTemplateModal] = useState({ isOpen: false });
  const [previewModal, setPreviewModal] = useState({ isOpen: false, invoice: null });
  const [emailModal, setEmailModal] = useState({ isOpen: false, invoice: null });
  const [showManualForm, setShowManualForm] = useState(false);

  const [invoices, setInvoices] = useState([
    {
      id: 1,
      invoiceNumber: 'INV-2026-001',
      customerName: 'Acme Corp',
      customerEmail: 'billing@acmecorp.com',
      plan: 'Enterprise',
      amount: 14999,
      generatedDate: '2026-02-05T10:00:00',
      dueDate: '2026-03-05',
      status: 'sent',
      template: 'Professional Blue',
      paymentLink: 'https://pay.example.com/inv-001',
      viewed: true,
      viewedDate: '2026-02-05T14:30:00'
    },
    {
      id: 2,
      invoiceNumber: 'INV-2026-002',
      customerName: 'Tech Solutions Ltd',
      customerEmail: 'accounts@techsolutions.com',
      plan: 'Scale',
      amount: 5999,
      generatedDate: '2026-02-04T09:00:00',
      dueDate: '2026-03-04',
      status: 'paid',
      template: 'Modern Minimal',
      paymentLink: 'https://pay.example.com/inv-002',
      viewed: true,
      viewedDate: '2026-02-04T10:15:00',
      paidDate: '2026-02-04T16:45:00'
    },
    {
      id: 3,
      invoiceNumber: 'INV-2026-003',
      customerName: 'StartupXYZ',
      customerEmail: 'finance@startupxyz.com',
      plan: 'Growth',
      amount: 2999,
      generatedDate: '2026-02-03T11:00:00',
      dueDate: '2026-03-03',
      status: 'draft',
      template: 'Classic',
      paymentLink: null,
      viewed: false
    }
  ]);

  const [templates, setTemplates] = useState([
    {
      id: 1,
      name: 'Professional Blue',
      thumbnail: '/templates/professional-blue.png',
      colorScheme: '#2563EB',
      isDefault: true
    },
    {
      id: 2,
      name: 'Modern Minimal',
      thumbnail: '/templates/modern-minimal.png',
      colorScheme: '#10B981',
      isDefault: false
    },
    {
      id: 3,
      name: 'Classic',
      thumbnail: '/templates/classic.png',
      colorScheme: '#6B7280',
      isDefault: false
    }
  ]);

  const tabs = [
    { id: 'generated', label: 'Generated Invoices', icon: 'FileCheck' },
    { id: 'templates', label: 'Templates', icon: 'Layout' },
    { id: 'automation', label: 'Automation', icon: 'Zap' },
    { id: 'manual', label: 'Create Manual', icon: 'Plus' }
  ];

  const handlePreview = (invoice) => {
    setPreviewModal({ isOpen: true, invoice });
  };

  const handleSendEmail = (invoice) => {
    setEmailModal({ isOpen: true, invoice });
  };

  const handleDownloadPDF = (invoice) => {
    console.log('Downloading PDF for:', invoice?.invoiceNumber);
  };

  const confirmEmailSend = (emailData) => {
    console.log('Sending email:', emailData);
    setInvoices(invoices?.map(inv => 
      inv?.id === emailModal?.invoice?.id 
        ? { ...inv, status: 'sent' }
        : inv
    ));
    setEmailModal({ isOpen: false, invoice: null });
  };

  const filteredInvoices = invoices?.filter(invoice => {
    const matchesSearch = invoice?.customerName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         invoice?.invoiceNumber?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice?.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const config = {
      draft: { bg: 'bg-muted/50', text: 'text-muted-foreground', label: 'Draft', icon: 'FileEdit' },
      sent: { bg: 'bg-info/10', text: 'text-info', label: 'Sent', icon: 'Send' },
      viewed: { bg: 'bg-primary/10', text: 'text-primary', label: 'Viewed', icon: 'Eye' },
      paid: { bg: 'bg-success/10', text: 'text-success', label: 'Paid', icon: 'CheckCircle' },
      overdue: { bg: 'bg-destructive/10', text: 'text-destructive', label: 'Overdue', icon: 'AlertCircle' }
    };
    const statusConfig = config?.[status] || config?.draft;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.bg} ${statusConfig?.text}`}>
        <Icon name={statusConfig?.icon} size={12} />
        {statusConfig?.label}
      </span>
    );
  };

  const analytics = {
    totalInvoices: invoices?.length,
    sentToday: invoices?.filter(i => new Date(i.generatedDate)?.toDateString() === new Date()?.toDateString())?.length,
    totalAmount: invoices?.reduce((sum, i) => sum + i?.amount, 0),
    paidInvoices: invoices?.filter(i => i?.status === 'paid')?.length
  };

  const renderGeneratedTab = () => (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder="Search customer or invoice..."
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e?.target?.value)}
            className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </select>
          <Button
            variant="outline"
            size="md"
            iconName="Download"
            iconPosition="left"
          >
            Export Report
          </Button>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Invoice #</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Customer</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Plan</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Amount</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Generated</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices?.map((invoice) => (
              <tr key={invoice?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                <td className="px-4 py-4 font-mono text-sm font-semibold text-foreground">{invoice?.invoiceNumber}</td>
                <td className="px-4 py-4">
                  <div>
                    <p className="font-medium text-foreground">{invoice?.customerName}</p>
                    <p className="text-xs text-muted-foreground">{invoice?.customerEmail}</p>
                  </div>
                </td>
                <td className="px-4 py-4 text-foreground">{invoice?.plan}</td>
                <td className="px-4 py-4 font-semibold text-foreground">₹{invoice?.amount?.toLocaleString()}</td>
                <td className="px-4 py-4 text-foreground">{new Date(invoice.generatedDate)?.toLocaleDateString()}</td>
                <td className="px-4 py-4">{getStatusBadge(invoice?.status)}</td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePreview(invoice)}
                      className="p-2 hover:bg-muted rounded-md transition-smooth"
                      title="Preview"
                    >
                      <Icon name="Eye" size={16} className="text-primary" />
                    </button>
                    <button
                      onClick={() => handleDownloadPDF(invoice)}
                      className="p-2 hover:bg-muted rounded-md transition-smooth"
                      title="Download PDF"
                    >
                      <Icon name="Download" size={16} className="text-success" />
                    </button>
                    {invoice?.status !== 'sent' && (
                      <button
                        onClick={() => handleSendEmail(invoice)}
                        className="p-2 hover:bg-muted rounded-md transition-smooth"
                        title="Send Email"
                      >
                        <Icon name="Mail" size={16} className="text-info" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {filteredInvoices?.map((invoice) => (
          <div key={invoice?.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-mono text-sm font-semibold text-foreground">{invoice?.invoiceNumber}</p>
                <p className="text-sm font-medium text-foreground mt-1">{invoice?.customerName}</p>
                <p className="text-xs text-muted-foreground">{invoice?.customerEmail}</p>
              </div>
              {getStatusBadge(invoice?.status)}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <p className="text-xs text-muted-foreground">Amount</p>
                <p className="text-sm font-semibold text-foreground">₹{invoice?.amount?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Generated</p>
                <p className="text-sm text-foreground">{new Date(invoice.generatedDate)?.toLocaleDateString()}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePreview(invoice)}
                iconName="Eye"
                iconPosition="left"
                fullWidth
              >
                Preview
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => handleDownloadPDF(invoice)}
                iconName="Download"
                iconPosition="left"
                fullWidth
              >
                Download
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTemplatesTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Customize invoice templates with your branding</p>
        <Button
          variant="default"
          size="md"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setTemplateModal({ isOpen: true })}
        >
          Create Template
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates?.map((template) => (
          <div key={template?.id} className="bg-card border border-border rounded-lg overflow-hidden hover:elevation-md transition-smooth">
            <div className="aspect-[4/3] bg-muted/30 flex items-center justify-center">
              <Icon name="FileText" size={48} className="text-muted-foreground" style={{ color: template?.colorScheme }} />
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-foreground">{template?.name}</h3>
                  {template?.isDefault && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary mt-1">
                      <Icon name="Star" size={10} />
                      Default
                    </span>
                  )}
                </div>
                <div className="w-6 h-6 rounded-full border-2 border-border" style={{ backgroundColor: template?.colorScheme }} />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" fullWidth iconName="Eye" iconPosition="left">
                  Preview
                </Button>
                <Button variant="default" size="sm" fullWidth iconName="Edit" iconPosition="left">
                  Edit
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAutomationTab = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Zap" size={20} className="text-primary" />
          Automated Invoice Generation
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
            <div>
              <p className="font-medium text-foreground">Subscription Billing</p>
              <p className="text-sm text-muted-foreground">Auto-generate invoices for recurring subscriptions</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
          <div className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
            <div>
              <p className="font-medium text-foreground">Usage-Based Charges</p>
              <p className="text-sm text-muted-foreground">Calculate and invoice based on usage metrics</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
          <div className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
            <div>
              <p className="font-medium text-foreground">Promotional Adjustments</p>
              <p className="text-sm text-muted-foreground">Apply discounts and promotional codes automatically</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Mail" size={20} className="text-primary" />
          Email Delivery Settings
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Email Template</label>
            <select className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50">
              <option>Professional Invoice Template</option>
              <option>Simple Invoice Template</option>
              <option>Detailed Invoice Template</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Delivery Tracking</label>
            <div className="flex items-center gap-2">
              <input type="checkbox" className="w-4 h-4 rounded border-border" defaultChecked />
              <span className="text-sm text-foreground">Track email opens and clicks</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Retry Mechanism</label>
            <div className="flex items-center gap-2">
              <input type="checkbox" className="w-4 h-4 rounded border-border" defaultChecked />
              <span className="text-sm text-foreground">Auto-retry failed deliveries (max 3 attempts)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar isCollapsed={sidebarCollapsed} />
      <div className={`flex-1 transition-smooth ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="p-4 md:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading flex items-center gap-3">
              <Icon name="FileText" size={32} className="text-primary" />
              Invoice Generation Center
            </h1>
            <p className="text-sm md:text-base text-muted-foreground font-body mt-2">
              Manage invoice templates, automated generation, and delivery workflows
            </p>
          </div>

          {/* Analytics Dashboard */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="FileText" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Invoices</p>
                  <p className="text-xl font-bold text-foreground">{analytics?.totalInvoices}</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-info/10 flex items-center justify-center">
                  <Icon name="Send" size={20} className="text-info" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Sent Today</p>
                  <p className="text-xl font-bold text-foreground">{analytics?.sentToday}</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Paid</p>
                  <p className="text-xl font-bold text-foreground">{analytics?.paidInvoices}</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="DollarSign" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Amount</p>
                  <p className="text-lg font-bold text-foreground">₹{analytics?.totalAmount?.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="mb-6 border-b border-border overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-smooth border-b-2 whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span className="hidden sm:inline">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="transition-smooth">
            {activeTab === 'generated' && renderGeneratedTab()}
            {activeTab === 'templates' && renderTemplatesTab()}
            {activeTab === 'automation' && renderAutomationTab()}
            {activeTab === 'manual' && <ManualInvoiceForm />}
          </div>
        </div>
      </div>
      {/* Modals */}
      <TemplateLibraryModal
        isOpen={templateModal?.isOpen}
        onClose={() => setTemplateModal({ isOpen: false })}
      />
      <InvoicePreviewModal
        isOpen={previewModal?.isOpen}
        invoice={previewModal?.invoice}
        onClose={() => setPreviewModal({ isOpen: false, invoice: null })}
        onDownload={handleDownloadPDF}
        onSendEmail={handleSendEmail}
      />
      <EmailDeliveryModal
        isOpen={emailModal?.isOpen}
        invoice={emailModal?.invoice}
        onClose={() => setEmailModal({ isOpen: false, invoice: null })}
        onConfirm={confirmEmailSend}
      />
    </div>
  );
};

export default InvoiceGenerationCenter;